import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiZap } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const PilotBanner = () => {
  const scrollToBooking = () => {
    document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-green-600/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-600/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-semibold mb-4">
            <SafeIcon icon={FiZap} className="w-4 h-4" />
            14-Day Free Pilot Program
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">Try It Before You Pay</h2>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
            We set up your AI agent, integrate it with your systems, and route real calls to it — at no cost for 14 days.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-10 items-start mb-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20"
          >
            <h3 className="text-xl md:text-2xl font-bold mb-5">What You Get in the Pilot:</h3>
            <div className="space-y-3">
              {[
                'Full AI agent setup — custom voice, scripts, and routing',
                'Complete integration (Zendesk, HubSpot, Salesforce, Freshdesk, etc.)',
                'Real calls handled — not a demo, not a sandbox',
                'Live dashboard with call recordings and transcripts',
                'Weekly optimization from our team',
                'Direct access to the founder throughout the pilot',
                'No setup fee, no monthly charge during the 14 days',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-slate-100 text-sm md:text-base leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/20">
              <h3 className="text-xl font-bold mb-4">What We Ask In Return:</h3>
              <ul className="space-y-3 text-slate-200">
                {[
                  "Honest feedback on what's working and what isn't",
                  'A brief call at the end of the pilot to review results',
                  'A testimonial if you\'re satisfied (optional)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">•</span>
                    <span className="text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 text-center">
              <div className="text-sm font-bold text-green-400 mb-2">AFTER THE PILOT</div>
              <div className="text-lg font-bold mb-1">Choose a plan that fits your volume</div>
              <div className="text-sm text-slate-300">Starting at $500/month. Month-to-month. Cancel anytime.</div>
            </div>
          </motion.div>
        </div>

        <div className="text-center">
          <button
            onClick={scrollToBooking}
            className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white px-10 py-4 rounded-xl text-lg font-bold transition-colors shadow-xl"
          >
            Start Free Pilot — Book a Discovery Call
          </button>
          <p className="text-slate-300 text-sm mt-4">15-minute call. No commitment. We'll scope your pilot on the call.</p>
        </div>
      </div>
    </section>
  );
};

export default PilotBanner;